import React, { useState, useEffect, useCallback } from 'react';
import { flushSync } from 'react-dom';
import { CompanyData } from '@/types';
import { StandardizedConnection, convertFileImportsToStandardizedConnections, Connection } from '@/types/connections';
import { Shield, Database, Loader2, Save } from 'lucide-react';
import ConnectorModal from './connectors/ConnectorModal';
import { fetchLinkedConnectors, removeConnector, syncFivetranConnector } from '../utils/api';
import { saveFormDraft, normalizeConnectionForStorage } from '@/utils/organization/saveOrganization';
import { plaidConnectionNeedsReauth } from '@/utils/plaidConnectionStatus';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/context/AuthContext';
import { supabase, invokeFunction } from '@/integrations/supabase/client';
import DataConnectionFormTooltip from './DataConnectionFormTooltip';

interface DataConnectionFormProps {
  companyData: CompanyData;
  onBack: () => void;
  onComplete: (linkedConnectors?: StandardizedConnection[]) => void;
  isFinalStep?: boolean;
  organisationId?: string;
  onCompanyDataUpdate?: (data: CompanyData) => void;
}

const DataConnectionForm: React.FC<DataConnectionFormProps> = ({
  companyData,
  onBack,
  onComplete,
  isFinalStep = false,
  organisationId,
  onCompanyDataUpdate
}) => {
  const { toast } = useToast();
  const { user } = useAuth();

  // Detect redirect return synchronously before any useEffect runs to avoid stale UI flash
  const isRedirectReturn =
    sessionStorage.getItem('connectorRedirect') === 'true' &&
    !!sessionStorage.getItem('savedFormStep') &&
    !!sessionStorage.getItem('selectedConnectorId');

  const [isModalOpen, setIsModalOpen] = useState(isRedirectReturn);
  const [linkedConnectors, setLinkedConnectors] = useState<StandardizedConnection[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingConnections, setIsLoadingConnections] = useState(false);
  const [isLoadingAfterRedirect, setIsLoadingAfterRedirect] = useState(isRedirectReturn);
  const [isRemoving, setIsRemoving] = useState<string | null>(null);
  const [connectionIds, setConnectionIds] = useState<string[]>([]);
  const [hasAttemptedConnection, setHasAttemptedConnection] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [connectionsLoaded, setConnectionsLoaded] = useState(false);
  const [previousConnectors, setPreviousConnectors] = useState<StandardizedConnection[]>([]);

  const resolveOrganisationId = (): string | undefined => {
    if (organisationId) return organisationId;
    const savedData = sessionStorage.getItem('figabl_company_data');
    if (!savedData) return undefined;
    try {
      const parsed = JSON.parse(savedData) as { organization_id?: string; organisation_id?: string };
      return parsed.organization_id || parsed.organisation_id;
    } catch {
      return undefined;
    }
  };

  const dedupeConnectorsById = (connectors: StandardizedConnection[]): StandardizedConnection[] => {
    const seen = new Map<string, StandardizedConnection>();
    for (const conn of connectors) {
      seen.set(conn.id, conn);
    }
    return Array.from(seen.values());
  };

  const applyFivetranConnectionMap = (connectors: StandardizedConnection[]): StandardizedConnection[] => {
    const connectionMap = JSON.parse(sessionStorage.getItem('connectionMap') || '{}') as Record<string, string>;
    return connectors.map((connector) => {
      if (connector.service === 'plaid') {
        return {
          ...connector,
          connectionId: connector.id,
        };
      }
      const mappedConnectionId = connectionMap[connector.connectorId];
      return {
        ...connector,
        connectionId: mappedConnectionId || connector.connectionId || connector.id,
      };
    });
  };

  const syncSessionConnectionIds = (connectors: StandardizedConnection[]) => {
    const ids = connectors.map((c) => c.id).filter(Boolean);
    sessionStorage.setItem('figabl_connection_ids', JSON.stringify(ids));
    setConnectionIds(ids);
  };

  // Helper function to convert StandardizedConnection to Connection
  const convertToConnections = (standardizedConnections: StandardizedConnection[]) => {
    return standardizedConnections.map(conn => ({
      id: conn.id,
      name: conn.name,
      type: conn.type,
      status: conn.status,
      created_at: conn.created_at,
      connectionId: conn.connectionId,
      metadata: conn.metadata,
      import_type: conn.import_type,
      ecommerce_subtype: conn.ecommerce_subtype,
      file_name: conn.file_name
    }));
  };

  // Fetch non-CSV file imports only (PDFs, images, documents, etc.)
  const fetchNonCsvFileImports = async (connectionIds: string[]): Promise<StandardizedConnection[]> => {
    try {
      if (connectionIds.length === 0) return [];

      const response = await invokeFunction(supabase, 'fetch-non-csv-file-imports', {
        body: { connectionIds }
      });

      if (response.error) {
        console.error('Error fetching file imports via edge function:', response.error);
        return [];
      }
      if (!response.data?.success) {
        console.error('Edge function returned failure for file imports:', response.data);
        return [];
      }

      const fileImports = response.data?.data || [];
      return convertFileImportsToStandardizedConnections(fileImports);
    } catch (error) {
      console.error('Failed to fetch file imports:', error);
      return [];
    }
  };

  // Helper function to detect newly connected Fivetran connectors
  const detectNewlyConnectedFivetranConnectors = (
    previousConnectors: StandardizedConnection[],
    currentConnectors: StandardizedConnection[]
  ): StandardizedConnection[] => {
    return currentConnectors.filter(current => {
      // Only check Fivetran connectors (not CSV imports or file uploads)
      if (current.service === 'csv' || !current.connectorId || current.connectorId.startsWith('csv-import-')) {
        return false;
      }

      // Find the previous state of this connector
      const previous = previousConnectors.find(prev => prev.connectorId === current.connectorId);

      // If it's a new connector with connected status, or status changed from pending to connected
      return current.status === 'connected' && (!previous || previous.status !== 'connected');
    });
  };

  // Function to trigger sync for newly connected Fivetran connectors
  const triggerSyncForNewConnectors = async (newlyConnectedConnectors: StandardizedConnection[]) => {
    const isConnectorRedirect = sessionStorage.getItem('connectorRedirect') === 'true';

    if (!isConnectorRedirect || newlyConnectedConnectors.length === 0) {
      return;
    }

    for (const connector of newlyConnectedConnectors) {
      try {
        await syncFivetranConnector(connector.connectorId);

      } catch (error) {
        console.error(`Error triggering sync for ${connector.connectorId}:`, error);

      }
    }
  };

  useEffect(() => {
    loadAllConnections()
  }, [toast, organisationId]);

  useEffect(() => {
    const checkRedirectAndConnection = async () => {
      setConnectionsLoaded(false)

      const savedStep = sessionStorage.getItem('savedFormStep');
      const isConnectorRedirect = sessionStorage.getItem('connectorRedirect') === 'true';
      const selectedConnectorId = sessionStorage.getItem('selectedConnectorId');
      const isReconnect = sessionStorage.getItem('isReconnect') === 'true';

      if (savedStep && isConnectorRedirect && selectedConnectorId) {
        setIsLoadingAfterRedirect(true);
        setIsModalOpen(true);

        // Fetch fresh connector status first so we can check whether reauth actually completed
        console.log(`[checkRedirectAndConnection] Fetching fresh connector status | connectorId=${selectedConnectorId} isReconnect=${isReconnect}`);
        const freshConnectors = await loadLinkedConnectors([selectedConnectorId]);
        console.log(`[checkRedirectAndConnection] Fresh connectors loaded | count=${freshConnectors?.length}`);

        // If this was a reconnect attempt, only mark as completed if Fivetran confirms it's healthy
        if (isReconnect) {
          const freshConnector = freshConnectors.find(
            c => c.connectorId === selectedConnectorId || c.id === selectedConnectorId
          );
          // For Amazon connections, the backend always forces needs_reconnect=true until
          // mark-amazon-reauth completes — creating a deadlock. Trust Fivetran's setup_state directly instead.
          const isAmazonService = freshConnector?.service === 'amazon_selling_partner' || freshConnector?.service === 'google_sheets';
          const freshSetupState = (freshConnector as any)?.setup_state ?? (freshConnector?.metadata?.status as any)?.setup_state;
          const isNowHealthy = freshConnector && (
            isAmazonService
              ? freshSetupState === 'connected'
              : !freshConnector.needs_reconnect
          );

          console.log(`[checkRedirectAndConnection] Reconnect check | freshConnector.service=${freshConnector?.service} needs_reconnect=${freshConnector?.needs_reconnect} setup_state=${freshSetupState} isNowHealthy=${isNowHealthy}`);

          if (isNowHealthy) {
            console.log(`[checkRedirectAndConnection] Connector is healthy — calling mark-amazon-reauth`);
            try {
              await invokeFunction(supabase, 'mark-amazon-reauth', {
                body: { connectorId: selectedConnectorId, email: user?.email || null }
              });
              // Reload all connectors so the UI reflects the newly-cleared needs_reconnect status
              // (backend now finds the reauth record and skips the override)
              console.log(`[checkRedirectAndConnection] mark-amazon-reauth succeeded — reloading connectors`);
              await loadAllConnections();
            } catch (err) {
              console.error('Failed to mark Amazon reauth as completed:', err);
            }
          } else {
            console.log(`[checkRedirectAndConnection] Reauth not marked — connector still needs reconnect or was not found`);
          }
          sessionStorage.removeItem('isReconnect');
        }

        setIsLoadingAfterRedirect(false);
        sessionStorage.setItem('connectorRedirect', 'false');
      }
    };

    if (connectionsLoaded)
      checkRedirectAndConnection()
  }, [connectionsLoaded])

  const [connectorIncomplete, setConnectorIncomplete] = useState(false)
  useEffect(() => {
    if (!linkedConnectors?.length) return
    const hasIncompleteConnector = linkedConnectors.some(
      (connector) => connector.status !== 'connected' || plaidConnectionNeedsReauth(connector),
    );
    setConnectorIncomplete(hasIncompleteConnector);
  }, [linkedConnectors])

  const beginConnectionsRefresh = useCallback(() => {
    flushSync(() => {
      setIsLoadingConnections(true);
    });
  }, []);

  const loadAllConnections = async () => {
    setIsLoading(true);
    setIsLoadingConnections(true);
    try {
      // Store previous state for comparison
      setPreviousConnectors([...linkedConnectors]);

      const allConnectionIds: string[] = [];
      const storedConnections = sessionStorage.getItem('figabl_connection_ids');
      const storedCsvConnections = sessionStorage.getItem('figabl_csv_connection_ids');

      // ... keep existing code (connection ID collection logic)
      if (storedConnections) {
        const parsedConnections = JSON.parse(storedConnections);
        if (Array.isArray(parsedConnections)) {
          allConnectionIds.push(...parsedConnections);
        }
      }
      if (storedCsvConnections) {
        const parsedCsvConnections = JSON.parse(storedCsvConnections);
        if (Array.isArray(parsedCsvConnections)) {
          allConnectionIds.push(...parsedCsvConnections);
        }
      }

      // Get connection IDs from company data (works for both drafts and organizations)
      if (companyData && companyData.connections) {
        if (Array.isArray(companyData.connections)) {
          for (const conn of companyData.connections) {
            if (conn && typeof conn === 'object' && 'id' in conn) {
              const connection = conn as Connection;
              if (!allConnectionIds.includes(connection.id)) {
                allConnectionIds.push(connection.id);
              }
            }
          }
        } else {
          // Handle object format
          for (const companyDataConn of Object.values(companyData.connections)) {
            if (companyDataConn && typeof companyDataConn === 'object' && 'id' in companyDataConn) {
              const connection = companyDataConn as Connection;
              if (!allConnectionIds.includes(connection.id)) {
                allConnectionIds.push(connection.id);
              }
            }
          }
        }
      }

      let fileConnections: StandardizedConnection[] = [];
      let organisationConnectionIds: string[] = [];

      if (companyData?.connections) {
        if (Array.isArray(companyData.connections)) {
          organisationConnectionIds = companyData.connections
            .filter(conn => conn && typeof conn === 'object' && 'id' in conn)
            .map(conn => (conn as Connection).id)
            .filter(id => !id.startsWith('csv-import-'));
        } else {
          organisationConnectionIds = Object.values(companyData.connections)
            .filter(conn => conn && typeof conn === 'object' && 'id' in conn)
            .map(conn => (conn as Connection).id)
            .filter(id => !id.startsWith('csv-import-'));
        }

        if (organisationConnectionIds.length > 0) {
          fileConnections = await fetchNonCsvFileImports(organisationConnectionIds);
        }
      }

      let fetchedConnectors: StandardizedConnection[] = [];
      if (allConnectionIds.length > 0) {
        fetchedConnectors = await fetchLinkedConnectors(allConnectionIds);
      }

      const merged = applyFivetranConnectionMap(
        dedupeConnectorsById([...fileConnections, ...fetchedConnectors]),
      );

      if (merged.length > 0) {
        setLinkedConnectors(merged);
        syncSessionConnectionIds(merged);
        setHasAttemptedConnection(true);

        const updatedCompanyData = {
          ...companyData,
          connections: merged.map(normalizeConnectionForStorage),
        };
        sessionStorage.setItem('figabl_company_data', JSON.stringify(updatedCompanyData));
        onCompanyDataUpdate?.(updatedCompanyData);
      } else {
        setLinkedConnectors([]);
        syncSessionConnectionIds([]);
      }
      setConnectionsLoaded(true)
    } catch (error) {
      console.error('Failed to load connections:', error);
      toast({
        title: "Warning",
        description: "Could not load existing connections. You may need to reconnect your data sources.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
      setIsLoadingConnections(false);
    }
  };

  const loadLinkedConnectors = async (connIds?: string[]): Promise<StandardizedConnection[]> => {
    if (!connIds || connIds.length === 0) {
      return [];
    }
    setIsLoading(true);
    try {
      // Store previous state before loading new connectors
      const prevConnectors = [...linkedConnectors];

      const connectors = await fetchLinkedConnectors(connIds);

      if (Array.isArray(connectors) && connectors.length > 0) {
        const combinedConnectors = [...linkedConnectors];
        connectors.forEach(connector => {
          const existingIndex = combinedConnectors.findIndex(c => c.id === connector.id);
          if (existingIndex >= 0) {
            // Replace with fresh data to clear stale needs_reconnect flags
            combinedConnectors[existingIndex] = connector;
          } else {
            combinedConnectors.push(connector);
          }
        });

        setLinkedConnectors(combinedConnectors);

        // Save connections to database before triggering sync
        try {

          if (!organisationId) {
            const savedData = sessionStorage.getItem('figabl_company_data');
            if (savedData) {
              const parsedData = JSON.parse(savedData);
              organisationId = parsedData?.organization_id
            }
          }

          const organisationData = {
            poc_name: companyData.pocName,
            poc_email: companyData.pocEmail,
            poc_phone: companyData.pocPhone || null,
            poc_phone_verified: companyData.pocPhoneVerified || false,
            name: companyData.name,
            name_dbas: companyData.name_dbas || null,
            address: companyData.address || '',
            state_of_incorporation: companyData.stateOfIncorporation || '',
            ein: companyData.ein || '',
            products: companyData.products as unknown as JSON,
            connections: combinedConnectors.map(normalizeConnectionForStorage) as unknown as JSON,
            revenue_last_12_months: companyData.revenueLast12Months || null,
            status: 'draft' as const,
            organization_id: organisationId
          };

          await invokeFunction(supabase, 'organisations-manager-v2', {
            body: {
              action: 'save_draft',
              data: organisationData
            }
          });
        } catch (error) {
          console.error('Error saving connections to database:', error);
          // Continue with sync even if save fails
        }

        // Check for newly connected Fivetran connectors and trigger sync
        const newlyConnectedFivetranConnectors = detectNewlyConnectedFivetranConnectors(prevConnectors, combinedConnectors);
        if (newlyConnectedFivetranConnectors.length > 0) {
          await triggerSyncForNewConnectors(newlyConnectedFivetranConnectors);
        }

        // ... keep existing code (session storage and connection handling)
        const storedConnections = sessionStorage.getItem('figabl_connection_ids') || '[]';
        const existingConnIds = JSON.parse(storedConnections);
        const newConnIds = [...existingConnIds];
        connIds.forEach(connId => {
          if (!newConnIds.includes(connId)) {
            newConnIds.push(connId);
          }
        });
        sessionStorage.setItem('figabl_connection_ids', JSON.stringify(newConnIds));

        connectors.forEach(connector => {
          if (connector.connectorId && (connector.connectorId.startsWith('csv-import-') || connector.service === 'csv')) {
            const storedCsvConnections = sessionStorage.getItem('figabl_csv_connection_ids') || '[]';
            const existingCsvConnIds = JSON.parse(storedCsvConnections);
            if (!existingCsvConnIds.includes(connector.connectorId)) {
              existingCsvConnIds.push(connector.connectorId);
              sessionStorage.setItem('figabl_csv_connection_ids', JSON.stringify(existingCsvConnIds));
            }
          }
        });
        setHasAttemptedConnection(true);

        const mappedConnectors = applyFivetranConnectionMap(combinedConnectors);
        syncSessionConnectionIds(mappedConnectors);

        const updatedCompanyData = {
          ...companyData,
          connections: mappedConnectors.map(normalizeConnectionForStorage),
        };
        saveDraftWithConnections(updatedCompanyData);

        return mappedConnectors;
      }

      return [];
    } catch (error) {
      console.error('Failed to load linked connectors:', error);
      if (hasAttemptedConnection) {
        toast({
          title: "Error",
          description: "Failed to load connected data sources. Please refresh the page.",
          variant: "destructive"
        });
      }
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  const saveDraftWithConnections = async (updatedCompanyData: CompanyData) => {
    try {
      sessionStorage.setItem('figabl_company_data', JSON.stringify(updatedCompanyData));
      await saveFormDraft(updatedCompanyData);
      onCompanyDataUpdate?.(updatedCompanyData);
      toast({
        title: "Draft saved",
        description: "Your connections have been saved to your draft",
        variant: "default"
      });
    } catch (error) {
      console.error('Failed to save draft with connections:', error);
    }
  };

  const handleUpdateConnectors = (connectors: StandardizedConnection[]) => {
    const mapped = applyFivetranConnectionMap(dedupeConnectorsById(connectors));
    setLinkedConnectors(mapped);
    if (mapped.length > 0) {
      syncSessionConnectionIds(mapped);

      const updatedCompanyData = {
        ...companyData,
        connections: mapped.map(normalizeConnectionForStorage),
      };

      mapped.forEach((connector) => {
        const connId = connector.connectorId || connector.id;
        if (connId.startsWith('csv-import-') || connector.service === 'csv') {
          const storedCsvConnections = sessionStorage.getItem('figabl_csv_connection_ids') || '[]';
          const existingCsvConnIds = JSON.parse(storedCsvConnections) as string[];
          if (!existingCsvConnIds.includes(connId)) {
            existingCsvConnIds.push(connId);
            sessionStorage.setItem('figabl_csv_connection_ids', JSON.stringify(existingCsvConnIds));
          }
        }
      });

      saveDraftWithConnections(updatedCompanyData);
    }
  };

  const handleRemoveConnector = async (connectorId: string, connectionId: string) => {
    setIsRemoving(connectorId);
    try {
      await removeConnector(connectorId, connectionId, organisationId);

      setLinkedConnectors(prevConnectors => prevConnectors.filter(connector => connector.id !== connectorId));

      sessionStorage.removeItem('selectedConnectorId');
      sessionStorage.removeItem('selectedConnectorData');
      toast({
        title: "Success",
        description: "Connector removed successfully."
      });
    } catch (error) {
      console.error('Failed to remove connector:', error);
      toast({
        title: "Error",
        description: "Failed to remove connector. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsRemoving(null);
    }
  };

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      toast({
        title: "Processing",
        description: "Preparing your submission...",
        variant: "default"
      });
      if (onComplete) {
        onComplete(linkedConnectors);
      }
    } catch (error) {
      console.error('Failed to process data:', error);
      toast({
        title: "Error",
        description: "Failed to process your submission. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const buttonText = linkedConnectors.length > 0 ? "Manage" : "Share Data";
  const connectionMessage = linkedConnectors.length > 0 ? "Your data source has been connected successfully. You can manage your connections or add additional sources below." : "Click the button below to select your data source and establish a secure connection.";

  return (
    <div className="form-container">
      <div className="form-section">
        <h2 className="form-header">Connect Your Data</h2>

        <div className="bg-brand-primary bg-opacity-5 p-6 rounded-lg border border-brand-primary border-opacity-20 mb-6">
          <div className="flex items-start">
            <Shield className="text-brand-primary flex-shrink-0 mt-1 mr-4" />
            <div>
              <h3 className="text-lg font-medium text-brand-basic mb-2">Secure Data Connection</h3>
              <p className="text-gray-700 mb-2">Thank you for trusting us with your data.
                We take this responsibility seriously. We use bank-standard encryption and security protocols to ensure your data remains secure.</p>
              <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
                <li>End-to-end encryption for all data transfers</li>
                <li>Granular access controls</li>
                <li>No persistent storage of sensitive information</li>
              </ul>
              <p className="text-gray-700 mt-3 text-sm">
                We look to stay current with all third party vendor requirements on data collection, confidentiality and storage. If you see service agreements have changed or believe we have overlooked a control please tell us immediately by contacting datasecurity@figabl.com.
              </p>
            </div>
          </div>
        </div>

        <div className="flex flex-col items-center justify-center p-8 bg-gray-50 rounded-lg border border-gray-300">
          <Database className="h-12 w-12 text-brand-primary mb-4" />
          <h3 className="text-xl font-medium text-brand-basic mb-2">
            {linkedConnectors.length > 0 ? "Connection Established" : "Ready to Connect?"}
          </h3>
          <p className="text-center text-gray-600 mb-6 max-w-md">
            {connectionMessage}
          </p>
          {isLoading ? <div className="flex items-center space-x-2">
            <Loader2 className="h-5 w-5 animate-spin text-brand-primary" />
            <span>Loading connections...</span>
          </div> : <button type="button" onClick={handleOpenModal} className="form-button flex items-center">
            {buttonText}
          </button>}
        </div>
      </div>

      <div className="flex justify-between mt-6">
        <button type="button" onClick={onBack} className="form-button-outline">
          Back to Company Info
        </button>
        <DataConnectionFormTooltip
          connectorIncomplete={connectorIncomplete}
          linkedConnectorsLength={linkedConnectors.length}
          isSubmitting={isSubmitting}
          onClick={handleSubmit}
        >
          {isSubmitting ? <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Saving...
          </> : <>
            <Save className="mr-2 h-4 w-4" />
            Finish
          </>}
        </DataConnectionFormTooltip>
      </div>

      <ConnectorModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSuccess={handleUpdateConnectors}
        linkedConnectors={linkedConnectors}
        onRemoveConnector={handleRemoveConnector}
        onConnectStart={() => setHasAttemptedConnection(true)}
        isLoadingConnections={isLoadingConnections}
        isLoadingAfterRedirect={isLoadingAfterRedirect}
        onRefreshLinkedConnectors={loadAllConnections}
        onBeginConnectionsRefresh={beginConnectionsRefresh}
        organisationId={resolveOrganisationId()}
      />

    </div>
  );
};

export default DataConnectionForm;
